<template>
  <v-container class="pa-4">
    <!-- Loading state -->
    <template v-if="initialLoading">
      <v-row align="center" justify="center">
        <v-col cols="12" class="text-center">
          <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
          <div class="mt-4">{{ $t("common.action.loading") }}</div>
        </v-col>
      </v-row>
    </template>

    <!-- Main content -->
    <template v-else>
      <!-- Action buttons -->
      <v-row class="mt-3 mb-5 mr-1">
        <v-col cols="12" class="d-flex align-center">
          <v-spacer></v-spacer>
          <v-menu v-if="hasRoleAccessToAction([UserRoleStatus.ADMIN, UserRoleStatus.OWNER])">
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                color="secondary"
                append-icon="mdi-chevron-down"
                data-test="customer-actions-button"
              >
                {{ $t("common.action.actions") }}
              </v-btn>
            </template>
            <v-list>
              <v-list-item data-test="import-customers-action" @click="openImportDialog">
                <v-list-item-title>
                  <v-icon class="mr-2">mdi-file-import</v-icon>
                  {{ $t("common.action.importCustomers") }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <v-btn color="primary" class="ml-2" prepend-icon="mdi-plus" @click="createCustomer()">
            {{ $t("page.customer.createNewCustomer") }}
          </v-btn>
        </v-col>
      </v-row>

      <customer-search-component />

      <!-- Empty state -->
      <v-card v-if="!isSearchMode && !allCustomers?.length">
        <empty-card
          :mainText="$t('page.customer.noCustomers')"
          :buttonText="$t('page.customer.createNewCustomer')"
          :buttonColor="'primary'"
          @button-click="createCustomer"
        >
          <template #image>
            <v-img src="@/shared/assets/emptyCustomers.png" max-height="150px" min-height="75px" />
          </template>
        </empty-card>
      </v-card>

      <!-- Customers table -->
      <v-card v-else>
        <v-data-table
          :headers="headers"
          :items="displayedCustomers"
          hide-default-footer
          :loading="tableLoading"
          loading-text="Loading customers..."
          hover
          @click:row="goTo"
        >
          <template v-slot:no-data>
            <div class="pa-6">
              <v-alert v-if="showNoSearchResults" type="info" variant="tonal" border="start">
                {{ $t("page.customer.noCustomersFound") }}
              </v-alert>
            </div>
          </template>
          <template v-slot:item="{ item }">
            <tr @click="goTo($event, { item })" :key="item.id" style="cursor: pointer">
              <!-- Icon -->
              <td class="text-center">
                <v-icon color="#E0E0E0" size="32">mdi-account</v-icon>
              </td>

              <!-- Name & Email -->
              <td class="text-start">
                <div class="text-subtitle-1">
                  {{ item.name }}
                </div>
                <div class="text-body-2 text-grey">
                  {{ item.email || "-" }}
                </div>
              </td>

              <!-- Phone -->
              <td class="text-center">
                <span class="text-body-2">
                  {{ item.phone || "-" }}
                </span>
              </td>

              <!-- Date Created -->
              <td class="text-center">
                <span class="text-body-2" v-dateWithTime="item.created"></span>
              </td>

              <!-- Actions -->
              <td class="text-center">
                <v-menu>
                  <template v-slot:activator="{ props }">
                    <v-btn v-bind="props" icon flat @click.stop>
                      <v-icon color="grey">mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item
                      v-for="(action, index) in actionList"
                      :key="index"
                      :value="index"
                      @click.stop="action.action(String(item.id))"
                    >
                      <v-list-item-title>{{ action.title }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </td>
            </tr>
          </template>
        </v-data-table>

        <!-- Pagination controls -->
        <v-divider />
        <v-card-actions>
          <span
            v-if="
              isSearchMode &&
              (displayedCustomers.length || customersStore.hasMore || searchPage > 1)
            "
            class="text-caption text-medium-emphasis"
          >
            {{ $t("page.customer.searchPage", { page: searchPage }) }}
          </span>
          <v-spacer />
          <v-btn
            color="primary"
            @click="handlePrevPage"
            :disabled="disablePrevButton"
            :loading="tableLoading"
          >
            {{ $t("common.action.prev") }}
          </v-btn>
          <v-btn
            color="primary"
            @click="handleNextPage"
            :disabled="disableNextButton"
            :loading="tableLoading"
          >
            {{ $t("common.action.next") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </template>
  </v-container>

  <customer-dialog ref="customerDialog" :dialogAction="dialogAction" @updated="handleRefresh()" />
  <send-payment-link-dialog ref="sendDialog" />
</template>

<script lang="ts" setup>
import { useCustomersStore } from "@/customers/stores/customers";
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import CustomerDialog from "@/customers/components/customer/customerDialog.vue";
import SendPaymentLinkDialog from "@/customers/components/customer/sendPaymentLinkDialog.vue";
import i18n from "@/app/plugins/i18n";
import { useRouter } from "vue-router";
import EmptyCard from "@/shared/components/Common/EmptyCard.vue";
import CustomerSearchComponent from "@/shared/components/Common/CustomerSearchComponent.vue";
import { debounce } from "@/shared/utils/debounce";
import { hasRoleAccessToAction } from "@/shared/utils/role.utils";
import { UserRoleStatus } from "@/shared/enums/status.enum";
import type { Customer } from "@/customers/types/customer";

const customersStore = useCustomersStore();
const customerDialog = ref<null | { open: (id?: string) => null }>(null);
const sendDialog = ref<null | { open: (id?: string) => null }>(null);
const dialogAction = ref("");
const router = useRouter();
const isInitialized = ref(false);
const suppressQueryWatcher = ref(false);
const pendingQueryAfterReset = ref<string | null>(null);
const query = computed(() =>
  typeof customersStore.query === "string" ? customersStore.query.trim() : "",
);
const initialLoading = computed(
  () =>
    (!isInitialized.value || customersStore.loading) &&
    !customersStore.customers.length &&
    !query.value,
);
const allCustomers = computed(() => customersStore.customers);
const tableLoading = computed(() => customersStore.loading || customersStore.listLoading);
const searchPage = ref(1);
const hasCompletedSearch = ref(false);
const searchRequestFailed = ref(false);

// Pagination state
const currentPage = ref(1);
const itemsPerPage = 10;
const hasMore = computed(() => customersStore.hasMore);
const pageCursors = ref<Array<string | undefined>>([undefined]);
const searchPageTokens = ref<Array<string | undefined>>([undefined]);
const isSearchMode = computed(() => query.value.length > 0);
const displayedCustomers = computed(() => allCustomers.value);
const showNoSearchResults = computed(
  () =>
    isSearchMode.value &&
    hasCompletedSearch.value &&
    !searchRequestFailed.value &&
    !tableLoading.value &&
    displayedCustomers.value.length === 0,
);
const disablePrevButton = computed(() =>
  isSearchMode.value
    ? searchPage.value === 1 || tableLoading.value
    : currentPage.value === 1 || tableLoading.value,
);
const disableNextButton = computed(() => !hasMore.value || tableLoading.value);

const resetPaginationState = () => {
  currentPage.value = 1;
  pageCursors.value = [undefined];
  searchPage.value = 1;
  searchPageTokens.value = [undefined];
  hasCompletedSearch.value = false;
  searchRequestFailed.value = false;
};

const resetToFullList = async () => {
  debouncedSearch.cancel();
  pendingQueryAfterReset.value = null;
  suppressQueryWatcher.value = true;
  customersStore.setQuery("");
  customersStore.invalidateRequests();
  customersStore.clearCustomerResults();
  resetPaginationState();

  try {
    await loadCustomers("");
  } finally {
    suppressQueryWatcher.value = false;

    const pendingQuery = pendingQueryAfterReset.value;
    pendingQueryAfterReset.value = null;

    if (pendingQuery && pendingQuery === query.value) {
      void handleQueryChange(pendingQuery);
    }
  }
};

onMounted(async () => {
  await resetToFullList();
  isInitialized.value = true;
});

const loadCustomers = async (startingAfterCursor = "") => {
  const params: { limit: number; starting_after?: string } = {
    limit: itemsPerPage,
  };

  if (startingAfterCursor) {
    params.starting_after = startingAfterCursor;
  }

  await customersStore.listCustomers({ params });
};

const handleRefresh = async () => {
  customersStore.invalidateRequests();
  customersStore.clearCustomerResults();

  if (isSearchMode.value) {
    searchPage.value = 1;
    searchPageTokens.value = [undefined];
    hasCompletedSearch.value = false;
    searchRequestFailed.value = false;
    const result = await runSearch();
    if (result === "success") {
      hasCompletedSearch.value = true;
    } else if (result === "error") {
      searchRequestFailed.value = true;
    }
    return;
  }

  currentPage.value = 1;
  pageCursors.value = [undefined];
  hasCompletedSearch.value = false;
  searchRequestFailed.value = false;
  await loadCustomers("");
};

// Simplified headers without unnecessary columns
interface CustomerTableHeader {
  title: string;
  align: "start" | "center" | "end";
  key: string;
  sortable: boolean;
  width?: string;
}

const headers: CustomerTableHeader[] = [
  {
    title: "",
    align: "center",
    key: "icon",
    sortable: false,
    width: "60px",
  },
  {
    title: i18n.global.t("common.action.name"),
    align: "start",
    key: "name",
    sortable: true,
  },
  {
    title: i18n.global.t("common.action.phone"),
    align: "center",
    key: "phone",
    sortable: false,
  },
  {
    title: i18n.global.t("common.action.dateCreated"),
    align: "center",
    key: "created",
    sortable: true,
  },
  {
    title: "",
    align: "center",
    key: "menu",
    sortable: false,
    width: "60px",
  },
];

const actionList = [
  {
    title: i18n.global.t("common.action.edit"),
    action: (id: string) => editCustomer(id),
  },
  {
    title: i18n.global.t("common.action.delete"),
    action: (id: string) => deleteCustomer(id),
  },
];

// Customer dialog actions
const createCustomer = async () => {
  dialogAction.value = "create";
  customerDialog.value?.open();
};

const editCustomer = (id: string) => {
  dialogAction.value = "edit";
  customerDialog.value?.open(id);
};

const deleteCustomer = async (id: string) => {
  dialogAction.value = "delete";
  customerDialog.value?.open(id);
};

const goTo = (event: Event, context: { item: Customer }) => {
  router.push({ path: `/customer/${context.item.id}` });
};

const runSearch = async (pageToken?: string) => {
  const params: { limit: number; page?: string } = {
    limit: itemsPerPage,
  };

  if (pageToken) {
    params.page = pageToken;
  }

  return customersStore.searchCustomers({ params });
};

const debouncedSearch = debounce(async (expectedQuery: string) => {
  if (expectedQuery !== query.value) {
    return;
  }

  const result = await runSearch();
  if (expectedQuery !== query.value) {
    return;
  }

  if (result === "success") {
    hasCompletedSearch.value = true;
    searchRequestFailed.value = false;
  } else if (result === "error") {
    hasCompletedSearch.value = false;
    searchRequestFailed.value = true;
  }
}, 500);

onUnmounted(() => {
  debouncedSearch.cancel();
});

watch(query, (nextQuery, previousQuery) => {
  if (!isInitialized.value) {
    return;
  }

  if (suppressQueryWatcher.value) {
    pendingQueryAfterReset.value = nextQuery;
    return;
  }

  if (nextQuery === previousQuery) {
    return;
  }

  void handleQueryChange(nextQuery);
});

const handleQueryChange = async (nextQuery: string) => {
  debouncedSearch.cancel();
  customersStore.invalidateRequests();
  customersStore.clearCustomerResults();
  hasCompletedSearch.value = false;
  searchRequestFailed.value = false;

  if (!nextQuery) {
    currentPage.value = 1;
    pageCursors.value = [undefined];
    await loadCustomers("");
    return;
  }

  searchPage.value = 1;
  searchPageTokens.value = [undefined];
  debouncedSearch(nextQuery);
};

const handlePrevPage = async () => {
  if (isSearchMode.value) {
    await prevSearchPage();
    return;
  }

  await prevPage();
};

const handleNextPage = async () => {
  if (isSearchMode.value) {
    await nextSearchPage();
    return;
  }

  await nextPage();
};

const openImportDialog = () => {
  router.push({ name: "importData", query: { create: "customers" } });
};

// Pagination methods
const prevPage = async () => {
  if (currentPage.value > 1 && !tableLoading.value) {
    const targetPage = currentPage.value - 1;
    const targetCursor = pageCursors.value[targetPage - 1];
    await loadCustomers(targetCursor);
    currentPage.value = targetPage;
    pageCursors.value = pageCursors.value.slice(0, targetPage);
  }
};

const nextPage = async () => {
  if (hasMore.value && !tableLoading.value) {
    const nextCursor = customersStore.startingAfter;
    if (!nextCursor) {
      return;
    }

    if (pageCursors.value.length === currentPage.value) {
      pageCursors.value.push(nextCursor);
    } else {
      pageCursors.value[currentPage.value] = nextCursor;
    }

    await loadCustomers(nextCursor);
    currentPage.value++;
  }
};

const prevSearchPage = async () => {
  if (searchPage.value <= 1 || tableLoading.value) {
    return;
  }

  const targetPage = searchPage.value - 1;
  const targetToken = searchPageTokens.value[targetPage - 1];
  const result = await runSearch(targetToken);
  if (result !== "success") {
    if (result === "error") {
      searchRequestFailed.value = true;
    }
    return;
  }

  hasCompletedSearch.value = true;
  searchRequestFailed.value = false;
  searchPage.value = targetPage;
  searchPageTokens.value = searchPageTokens.value.slice(0, targetPage);
};

const nextSearchPage = async () => {
  if (!customersStore.hasMore || !customersStore.nextPage || tableLoading.value) {
    return;
  }

  const nextToken = customersStore.nextPage;
  const result = await runSearch(nextToken);
  if (result !== "success") {
    if (result === "error") {
      searchRequestFailed.value = true;
    }
    return;
  }

  hasCompletedSearch.value = true;
  searchRequestFailed.value = false;
  if (searchPageTokens.value.length === searchPage.value) {
    searchPageTokens.value.push(nextToken);
  } else {
    searchPageTokens.value[searchPage.value] = nextToken;
  }
  searchPage.value += 1;
};
</script>
